import { useMemo, useState } from 'react';

import Button from '@r1-frontend/ui-react/components/buttons/button';
import { WithVariantsOption } from '@r1-frontend/ui-react/components/OptionList';
import { isSelected } from '@r1-frontend/ui-react/components/OptionList/utils';
import { SingleArrowDown } from '@r1-frontend/ui-react/components/svg/arrows';
import { Paragraph4 } from '@r1-frontend/ui-react/components/typography/paragraph';
import { COLORS } from '@r1-frontend/ui-react/tokens/colors';
import { NBSP } from '@r1-frontend/ui-react/tokens/symbols';

import * as ST from './styled';

export interface ICustomOptions extends Omit<WithVariantsOption, 'value'> {
    value: string,
}

interface IProps {
    option: ICustomOptions,
    selectedOptionId: string | number,
    selectedVariantId: string | number,
    onClickOption: (optionId: string | number, variantId: string | number) => void,

    reverseVariants?: boolean,
    label?: string,
    labelPrefix?: string,
    description?: string,
}

const CustomOptionList = ({
    option,
    selectedOptionId,
    selectedVariantId,
    onClickOption,

    reverseVariants,
    labelPrefix = 'Далее',
    label = '',
    description,
}: IProps): JSX.Element => {
    const { id, value, variants } = option;

    const [isShowVariants, setIsShowVariants] = useState(false);

    const selectedVariantValue = useMemo(() => {
        return variants.find(variant => variant.id === selectedVariantId)?.valueStr || variants[0].valueStr;
    }, [selectedVariantId, variants]);

    const handleClick = () => {
        const [firtsVariant] = variants || [undefined];
        const variantId = firtsVariant?.id || '';

        if (!isSelected(`${id}`, `${selectedOptionId}`)) {
            onClickOption(id, variantId);
        }
    };

    const toggleIsShowVariants = () => {
        setIsShowVariants(prev => isSelected(`${id}`, `${selectedOptionId}`) ? !prev : true);
    };

    const labelText = label ? label : `${labelPrefix}${NBSP}${selectedVariantValue}`;

    return (
        <ST.RadioBtnWrap>
            <ST.SBaseRadio
                size='S'
                checked={selectedOptionId === id}
                onChange={handleClick}
                data-test='test-drive-option'
            >
                <ST.InnerWrap onClick={toggleIsShowVariants}>
                    <ST.ValueElem>{value}</ST.ValueElem>
                    <ST.LabelWithArrow>
                        <ST.LabelElem>{labelText}</ST.LabelElem>
                        {variants.length && (
                            <ST.IconWrap isRotate={isShowVariants}>
                                <SingleArrowDown size={16}/>
                            </ST.IconWrap>
                        )}
                    </ST.LabelWithArrow>
                </ST.InnerWrap>
            </ST.SBaseRadio>

            {variants.length && isShowVariants && (
                <ST.VariantsWrap>
                    {description && (
                        <Paragraph4 $color={COLORS.TextInfo} $smaller>{description}</Paragraph4>
                    )}
                    {variants.map(variant => (
                        <Button
                            key={variant.id}
                            bType={variant.id === selectedVariantId ? 'primary' : 'secondary'}
                            onClick={() => onClickOption(id, variant.id)}
                            data-test='variant'
                        >
                            <ST.InnerWrap reverse={reverseVariants}>
                                <ST.VariantElem>
                                    {variant.valueStr || variant.value}
                                </ST.VariantElem>
                                <ST.VariantElemLabel>
                                    {variant.label}
                                </ST.VariantElemLabel>
                            </ST.InnerWrap>
                        </Button>
                    ))}
                </ST.VariantsWrap>
            )}
        </ST.RadioBtnWrap>
    );
};

export default CustomOptionList;
